import PropTypes from 'prop-types';
import { TextField, Autocomplete, styled, Tooltip, Box } from '@mui/material';

import { ReactComponent as ArrowDownSelectIcon } from '../../assets/images/arrow-down-select.svg';

export const StyledSelect = styled(Autocomplete)(({ theme }) => ({
  '& > div > div': {
    zIndex: 2,
    borderRadius: 12,
    position: 'relative',
    fontSize: '1.125rem',
    lineHeight: '24px',
    fontWeight: 'normal',
    backgroundColor: theme.palette.common.white,
    height: 56,

    '&.Mui-error': {
      borderColor: theme.palette.error.main,
      background: theme.palette.error.light
    },

    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,

      '& > input': {
        '-webkit-text-fill-color': theme.palette.grey[1000]
      }
    },

    '& > input': {
      padding: '16px 16px',

      '&::placeholder': {
        color: theme.palette.grey[1000],
        opacity: 1
      }
    },

    '& > fieldset': {
      border: `0.5px solid ${theme.palette.action.disabledBackground}`
    },

    '&:hover': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.text.secondary
      }
    },

    '&:hover.Mui-disabled': {
      '& > fieldset': {
        borderColor: theme.palette.action.disabled
      }
    },

    '&.Mui-focused': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderWidth: '0.5px',
        borderColor: theme.palette.primary.main
      }
    },
    '&.Mui-focused::selection': {
      background: 'transparent'
    },

    '&.Mui-focused.Mui-error': {
      '& > fieldset.MuiOutlinedInput-notchedOutline': {
        borderWidth: '0.5px',
        borderColor: theme.palette.error.main
      }
    },

    '&.MuiInputBase-sizeSmall': {
      height: 36,
      overflow: 'hidden',

      '& > .multiple': {
        position: 'absolute',
        width: '80%',
        zIndex: -1
      },

      '& .MuiAutocomplete-tagSizeMedium': {
        marginTop: 0
      },

      '& > .MuiChip-root': {
        height: 25,
        margin: 0,
        marginLeft: 5
      },

      '& > input.MuiInputBase-input.MuiAutocomplete-input': {
        padding: '0px 6px',
        paddingRight: '20px',
        '&::selection': {
          background: 'transparent'
        }
      },

      '& > div.MuiInputAdornment-root': {
        '& + input': {
          fontWeight: 500
        }
      },

      '&.Mui-disabled > div.MuiInputAdornment-root': {
        '& > span': {
          color: theme.palette.grey[1000]
        }
      }
    },

    '& > div.MuiInputAdornment-root': {
      '& + input': {
        paddingLeft: 0
      }
    }
  },

  '& > .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '1rem'
  }
}));

export const CustomSelect = ({
  name,
  value,
  options,
  loading,
  setFieldValue,
  disabled,
  onChange,
  startAdornmentText,
  multiple,
  nameLabel = false,
  tooltip,
  ...props
}) => {
  const handleChange = (event, res) => {
    if (setFieldValue) {
      setFieldValue(name, res);
      onChange && onChange(event);
    } else {
      onChange(res);
    }
  };

  const StyledSelectToReturn =
    <StyledSelect
      id={name}
      name={name}
      limitTags={1}
      popupIcon={<ArrowDownSelectIcon />}
      autoComplete={false}
      options={options || []}
      value={value}
      disabled={disabled}
      className={multiple ? styled.multiple : ''}
      loading={loading}
      loadingText='Загрузка списка...'
      noOptionsText='Нет опций'
      getOptionLabel={(option) => {
        if (Array.isArray(option)) {
          return nameLabel ? option[`${nameLabel}`] || '' : option.label || option.name || '';
        }
        if (typeof option === 'string') {
          return option || '';
        }
        if (typeof option === 'object') {
          return nameLabel ? option[`${nameLabel}`] || '' : option.label || option.name || '';
        }
      }}
      isOptionEqualToValue={(option, value) => option.code === value.code || option.id === value.id}
      onChange={handleChange}
      disableClearable
      multiple={multiple}
      classes={{
        inputFocused: multiple ? 'multiple' : ''
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            // autoComplete="off"
            value={value || []}
            // onChange={handleChange}
            InputProps={{
              ...params.InputProps,

              ...(multiple && startAdornmentText
                ? {
                  startAdornment: [startAdornmentText, ...params.InputProps.startAdornment]
                }
                : { startAdornment: startAdornmentText })
            }}
            {...props}
          />
        );
      }}
      data-testid="CustomSelect"
    />;

  return tooltip && disabled ?
    <Tooltip title={tooltip} arrow sx={{pointerEvents: 'auto'}}>
      <Box component="span" data-testid="Wrapper">
        {StyledSelectToReturn}
      </Box>
    </Tooltip> :
    StyledSelectToReturn;
};

CustomSelect.propTypes = {
  options: PropTypes.any,
  value: PropTypes.any,
  name: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  startAdornmentText: PropTypes.node,
  nameLabel: PropTypes.string,
  tooltip: PropTypes.string,
};
